/**
 * GDPR region detection.
 *
 * The cookie consent banner is only legally required for visitors located in
 * the EU/EEA + UK + Switzerland. The primary audience of this site is
 * Egyptian, so showing the banner to everyone is intrusive UX without legal
 * benefit.
 *
 * Detection strategy (in order):
 *   1. Manual override via localStorage key `ahg.gdpr.force` ("on" | "off").
 *      Used by admin/QA to force-test either flow regardless of location.
 *   2. Timezone heuristic via Intl.DateTimeFormat — instant, no network,
 *      reliable enough for the EDPB's "reasonable best efforts" standard.
 *   3. If timezone is unknown/missing, default to TRUE (show banner) — the
 *      safe legal default.
 *
 * NOTE: This intentionally avoids any IP geolocation API call. A free API
 * adds a network dependency that can fail / be blocked / slow down first
 * paint. When the backend lands, server-side IP geolocation can replace this.
 */
// IANA timezone identifiers covering EU member states, EEA (Iceland,
// Liechtenstein, Norway), the UK, and Switzerland. Sourced from the standard
// IANA tz database.
const GDPR_TIMEZONES: ReadonlySet<string> = new Set([
// EU
'Europe/Vienna', // Austria
'Europe/Brussels', // Belgium
'Europe/Sofia', // Bulgaria
'Europe/Zagreb', // Croatia
'Asia/Famagusta', // Cyprus
'Asia/Nicosia', // Cyprus
'Europe/Prague', // Czechia
'Europe/Copenhagen', // Denmark
'Europe/Tallinn', // Estonia
'Europe/Helsinki', // Finland
'Europe/Paris', // France
'Europe/Berlin', // Germany
'Europe/Busingen', // Germany enclave in CH
'Europe/Athens', // Greece
'Europe/Budapest', // Hungary
'Europe/Dublin', // Ireland
'Europe/Rome', // Italy
'Europe/Riga', // Latvia
'Europe/Vilnius', // Lithuania
'Europe/Luxembourg', // Luxembourg
'Europe/Malta', // Malta
'Europe/Amsterdam', // Netherlands
'Europe/Warsaw', // Poland
'Europe/Lisbon', // Portugal
'Atlantic/Azores', // Portugal
'Atlantic/Madeira', // Portugal
'Europe/Bucharest', // Romania
'Europe/Bratislava', // Slovakia
'Europe/Ljubljana', // Slovenia
'Europe/Madrid', // Spain
'Africa/Ceuta', // Spain enclave
'Atlantic/Canary', // Spain
'Europe/Stockholm', // Sweden
// EEA non-EU
'Atlantic/Reykjavik', // Iceland
'Europe/Vaduz', // Liechtenstein
'Europe/Oslo', // Norway
'Arctic/Longyearbyen', // Norway (Svalbard)
// UK + Crown Dependencies
'Europe/London', // UK
'Europe/Belfast', // UK (deprecated alias, still seen)
'Europe/Jersey', // Jersey
'Europe/Guernsey', // Guernsey
'Europe/Isle_of_Man', // Isle of Man
'Europe/Gibraltar', // Gibraltar
// Switzerland (not EU/EEA but enforces FADP, near-equivalent)
'Europe/Zurich']
);
const OVERRIDE_KEY = 'ahg.gdpr.force';
/**
 * Best-effort detection of whether the current visitor is in a region that
 * requires a GDPR-style consent banner. Returns true on uncertainty (safe
 * default). Never throws.
 */
export function isInGdprRegion(): boolean {
  // 1) Manual override for testing or admin enforcement.
  try {
    const override =
    typeof window !== 'undefined' ?
    window.localStorage.getItem(OVERRIDE_KEY) :
    null;
    if (override === 'on') return true;
    if (override === 'off') return false;
  } catch {

    // localStorage may be unavailable (private mode) — ignore.
  } // 2) Timezone heuristic.
  try {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return true;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return true;
    return GDPR_TIMEZONES.has(tz);
  } catch {
    return true;
  }
}
/**
 * Event name fired on `window` when a user wants to reopen the cookie
 * settings banner (e.g. from a footer link). `CookieConsent` listens for it.
 */
export const OPEN_COOKIE_SETTINGS_EVENT = 'ahg:open-cookie-settings';
export function openCookieSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}